import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { Eyebrow, EmptyState, Loading, Panel, StatCard, StatusBadge, Tag } from '../components/ui';
import { QuotaCard } from '../components/QuotaCard';
import { fmtInt, fmtUsd, fmtDateTime, relativeTime } from '../lib/format';

export const UserDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const { data, loading, error } = useAsync(() => adminApi.user(id), [id]);

  if (loading && !data) return <Loading label="Loading user…" />;
  if (error || !data) return <EmptyState title="User not found" hint={error ?? undefined} />;

  const { user, quota, recipes, importJobs, providerBreakdown } = data;
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return (
    <div className="space-y-6">
      <Link to="/users" className="inline-flex items-center gap-1.5 text-sm text-cream-400 hover:text-saffron-300">
        ← All users
      </Link>

      <Panel className="rise flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-teal-500/15 font-mono text-lg text-teal-400 ring-1 ring-teal-500/25">
            {(user.email?.[0] ?? 'A').toUpperCase()}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-xl text-cream-50">{user.email ?? 'No email'}</h2>
              {user.anonymous && <Tag>anonymous</Tag>}
            </div>
            <div className="text-sm text-cream-400">
              {name && <span>{name} · </span>}
              Joined {fmtDateTime(user.createdAt)} · Active {relativeTime(user.lastActiveAt)}
            </div>
          </div>
        </div>
        <code className="rounded-lg border border-cream-600/15 bg-ink-950/60 px-3 py-1.5 font-mono text-xs text-cream-400">
          {user.id}
        </code>
      </Panel>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Recipes" value={fmtInt(user.recipeCount)} accent="teal" />
        <StatCard label="Import jobs" value={fmtInt(user.importJobCount)} accent="cream" />
        <StatCard label="Tokens" value={fmtInt(user.totalTokens)} />
        <StatCard label="Est. cost" value={fmtUsd(user.estimatedCostUsd)} />
      </div>

      <QuotaCard userId={user.id} initial={quota} />

      {providerBreakdown.length > 0 && (
        <Panel className="rise">
          <Eyebrow>Token usage by provider</Eyebrow>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {providerBreakdown.map((p) => (
              <div
                key={p.provider}
                className="flex items-center justify-between rounded-xl border border-cream-600/15 bg-ink-850/50 px-4 py-3"
              >
                <div>
                  <Tag tone="saffron">{p.provider}</Tag>
                  <div className="mt-1.5 text-xs text-cream-400">{fmtInt(p.calls)} calls</div>
                </div>
                <div className="text-right">
                  <div className="tnum text-sm text-cream-100">{fmtInt(p.totalTokens)}</div>
                  <div className="tnum text-xs text-cream-400">{fmtUsd(p.estimatedCostUsd)}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel className="rise" pad={false}>
          <div className="px-5 pt-5">
            <Eyebrow>Recipes</Eyebrow>
            <h3 className="font-display text-lg text-cream-50">Saved ({fmtInt(user.recipeCount)})</h3>
          </div>
          <div className="mt-3 max-h-96 divide-y divide-cream-600/10 overflow-y-auto">
            {recipes.length ? (
              recipes.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <span className="truncate text-sm text-cream-200" title={r.title}>
                    {r.title}
                  </span>
                  <div className="flex shrink-0 items-center gap-3">
                    <Tag>{r.sourceType}</Tag>
                    <span className="tnum text-xs text-cream-400">{relativeTime(r.createdAt)}</span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState title="No recipes" />
            )}
          </div>
        </Panel>

        <Panel className="rise" pad={false}>
          <div className="px-5 pt-5">
            <Eyebrow>Imports</Eyebrow>
            <h3 className="font-display text-lg text-cream-50">Jobs ({fmtInt(user.importJobCount)})</h3>
          </div>
          <div className="mt-3 max-h-96 divide-y divide-cream-600/10 overflow-y-auto">
            {importJobs.length ? (
              importJobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Tag>{j.sourceType}</Tag>
                    {j.error && (
                      <span className="truncate text-xs text-bad/80" title={j.error}>
                        {j.error}
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <StatusBadge status={j.status} />
                    <span className="tnum text-xs text-cream-400">{relativeTime(j.createdAt)}</span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState title="No import jobs" />
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
};
